/**
 * First-match guidance.
 *
 * Two things stopped a new player cold, both reported from an actual session:
 * they did not know the starting vehicle had to be **deployed** into a base,
 * and once a structure finished building they did not know it then had to be
 * **placed** on the ground. Neither is guessable, and neither is a tutorial's
 * worth of explanation — one line at the right moment is enough.
 *
 * The hints are derived from state rather than scripted, so they cannot get out
 * of step with what the player has actually done: a player who deploys before
 * reading anything simply never sees the first hint. Each one retires
 * permanently once its condition has been satisfied, which is what keeps this
 * from nagging in the twentieth match.
 *
 * **Presentation only.** It reads simulation state and writes DOM.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import { Entities, EF } from '../sim/entities.js'
import { Build } from '../sim/build.js'
import { defById } from '../sim/data/entities.js'

interface Hint {
  readonly id: string
  /** Shown while true and the hint has not retired. */
  active(ctx: HintState): boolean
  /** Once true, the hint never shows again. */
  done(ctx: HintState): boolean
  text(ctx: HintState): string
}

interface HintState {
  entities: Entities
  build: Build
  slot: number
  placing: boolean
  structures: number
  founders: number
  readyKind: number
}

const HINTS: Hint[] = [
  {
    id: 'deploy',
    active: (s) => s.founders > 0 && s.structures === 0,
    done: (s) => s.structures > 0,
    text: () => 'Click your vehicle, then press D to deploy it into a base.',
  },
  {
    id: 'place',
    active: (s) => s.readyKind > 0 && !s.placing,
    done: () => false,
    text: (s) =>
      `${defById(s.readyKind)?.name ?? 'A structure'} is built. Click its icon in the sidebar, ` +
      `then click a spot near your base to place it.`,
  },
  {
    id: 'placing',
    active: (s) => s.placing,
    done: () => false,
    text: () => 'Left-click to place. Green is legal, red is not. Esc puts it back on hold.',
  },
  {
    id: 'build',
    active: (s) => s.structures > 0 && s.readyKind === 0 && s.build.queueDepth(s.slot, 0) === 0,
    done: (s) => s.structures > 2,
    text: () => 'Pick a structure from the sidebar to start building. 1 2 3 4 switch tabs.',
  },
]

export class Hints implements Subsystem {
  static id = 'hints'
  static deps = ['entities', 'build'] as const

  private entities!: Entities
  private build!: Build
  private readonly el: HTMLDivElement
  private readonly retired = new Set<string>()
  private showing = ''

  /** Whose game to guide. */
  localSlot = 0
  /** Set by the placement subsystem, which knows if a footprint is on the cursor. */
  placing = false

  constructor(root: HTMLElement) {
    this.el = document.createElement('div')
    this.el.className = 'ba-hint'
    root.append(this.el)
  }

  init(ctx: Ctx): void {
    this.entities = ctx.get<Entities>('entities')
    this.build = ctx.get<Build>('build')
    // Nothing left to advise once the match is decided, and a hint under the
    // victory screen reads as the game not having noticed.
    ctx.on('match:over', () => {
      this.finished = true
      this.show('')
    })
  }

  private finished = false

  update(): void {
    if (this.finished) return
    const state = this.read()
    for (const hint of HINTS) {
      if (this.retired.has(hint.id)) continue
      if (hint.done(state)) {
        this.retired.add(hint.id)
        continue
      }
      if (!hint.active(state)) continue
      this.show(hint.text(state))
      return
    }
    this.show('')
  }

  private read(): HintState {
    let structures = 0
    let founders = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== this.localSlot) continue
      if (this.entities.has(s, EF.Structure)) structures++
      else if (this.entities.has(s, EF.Founder)) founders++
    }
    return {
      entities: this.entities,
      build: this.build,
      slot: this.localSlot,
      placing: this.placing,
      structures,
      founders,
      readyKind: this.build.readyKindAt(this.localSlot, 0),
    }
  }

  private show(text: string): void {
    if (text === this.showing) return
    this.showing = text
    this.el.textContent = text
    this.el.classList.toggle('visible', text !== '')
  }

  dispose(): void {
    this.el.remove()
  }
}
